import { useColorMode } from '@vueuse/core'
import type { ComputedRef, Ref } from 'vue'

export type ThemePreference = 'light' | 'dark' | 'auto'
export type ResolvedTheme = 'light' | 'dark'

export interface UseThemeReturn {
  /** The persisted user selection ('auto' means "System"). Drives the menu checkmarks. */
  theme: Ref<ThemePreference>
  /** What's actually applied to <html> (resolves 'auto' to the current system preference). */
  resolvedTheme: ComputedRef<ResolvedTheme>
  /** Live OS color-scheme preference. */
  systemTheme: ComputedRef<ResolvedTheme>
  /** Persist a new preference and apply it immediately. */
  setTheme: (value: ThemePreference) => void
}

const VALID_THEMES: ThemePreference[] = ['light', 'dark', 'auto']

// An unexpected/corrupted stored value (e.g. leftover from an older format) falls
// back to following the system preference, matching the previous behavior.
if (typeof localStorage !== 'undefined') {
  const stored = localStorage.getItem('color-theme')
  if (stored && !VALID_THEMES.includes(stored as ThemePreference)) {
    localStorage.setItem('color-theme', 'auto')
  }
}

// Module-level singleton: created once on import so the `dark` class is applied
// synchronously before the first paint (see main.ts).
const colorMode = useColorMode({
  storageKey: 'color-theme',
  modes: {
    light: '',
    dark: 'dark',
  },
})

// Keep store sanitized if modified elsewhere
if (!VALID_THEMES.includes(colorMode.store.value as ThemePreference)) {
  colorMode.store.value = 'auto'
}

export function useTheme(): UseThemeReturn {
  return {
    theme: colorMode.store,
    resolvedTheme: colorMode.state,
    systemTheme: colorMode.system,
    setTheme(value: ThemePreference) {
      colorMode.value = value
    },
  }
}
